import React, { useState, useEffect, useCallback } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { withStyles } from '@material-ui/core/styles'
import {
  Typography,
  Stepper,
  Step,
  StepLabel,
  Paper,
  Button,
  Box,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  CircularProgress,
  Tooltip,
  IconButton
} from '@material-ui/core'
import { useSnackbar } from 'notistack'
import SettingsIcon from '@material-ui/icons/Settings'
import KeyboardArrowDownIcon from '@material-ui/icons/KeyboardArrowDown'
import KeyboardArrowUpIcon from '@material-ui/icons/KeyboardArrowUp'
import CheckCircleIcon from '@material-ui/icons/CheckCircle'
import OpenInNewIcon from '@material-ui/icons/OpenInNew'
import FileCopyIcon from '@material-ui/icons/FileCopy'
import { CopyToClipboard } from 'react-copy-to-clipboard'
import Collapse from '@material-ui/core/Collapse'
import { collectionTranslateAction } from 'src/reducers/course-collection/action'
import { getCollectionList } from 'src/reducers/collection/action'
import {
  startConnection,
  addTranslationCompleteListener,
  removeTranslationCompleteListener,
} from 'src/utils/signalR-connection/signalR-connection'
import { languages } from 'src/model/languages'
import URL from 'src/api/url/api'

import { SettingsPanel } from './components'
import styles from './styles'

const slugify = (text) => {
  return (text || '')
    .toLowerCase()
    .replace(/[^a-z0-9\s-]/g, '')
    .replace(/\s+/g, '-')
    .replace(/-+/g, '-')
    .trim()
}

const LinkCard = ({ classes, label, url, isExternal }) => {
  const [copied, setCopied] = useState(false)

  const handleCopy = () => {
    setCopied(true)
    setTimeout(() => setCopied(false), 2000)
  }

  return (
    <div className={classes.linkCard}>
      <Typography className={classes.linkLabel}>{label}</Typography>
      <div className={classes.linkContent}>
        <Typography className={classes.linkUrl} title={url}>
          {url}
        </Typography>
        <div className={classes.linkActions}>
          <CopyToClipboard text={url} onCopy={handleCopy}>
            <Tooltip title={copied ? 'Copied!' : 'Copy to clipboard'}>
              <IconButton className={classes.actionIconBtn}>
                <FileCopyIcon fontSize="small" className={copied ? classes.copySuccess : ''} />
              </IconButton>
            </Tooltip>
          </CopyToClipboard>
          <Tooltip title="Open link">
            <IconButton
              className={classes.actionIconBtn}
              component="a"
              href={url}
              target={isExternal ? '_blank' : undefined}
              rel={isExternal ? 'noopener noreferrer' : undefined}
            >
              <OpenInNewIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        </div>
      </div>
    </div>
  )
}

const STEPS = [
  'Select learning path',
  'Configure Translation',
  'Translate'
]

const CollectionTranslate = ({ classes }) => {
  const dispatch = useDispatch()
  const { enqueueSnackbar } = useSnackbar()

  const collections = useSelector(state => state.collection.list || [])

  const [activeStep, setActiveStep] = useState(0)
  const [selectedCollectionId, setSelectedCollectionId] = useState('')
  const [selectedLanguage, setSelectedLanguage] = useState('es')
  const [settingsOpen, setSettingsOpen] = useState(false)
  const [isTranslating, setIsTranslating] = useState(false)
  const [loadingCollections, setLoadingCollections] = useState(false)
  const [targetCollectionId, setTargetCollectionId] = useState('')
  const [isSuccess, setIsSuccess] = useState(false)
  const [translatedName, setTranslatedName] = useState('')

  // Use ref to avoid closure staleness in signalR callback
  const targetCollectionIdRef = React.useRef(targetCollectionId)
  useEffect(() => {
    targetCollectionIdRef.current = targetCollectionId
  }, [targetCollectionId])

  // Fetch collections on mount
  useEffect(() => {
    setLoadingCollections(true)
    dispatch(getCollectionList({ currentPage: 0, pageSize: 100 }, () => {
      setLoadingCollections(false)
    }))
  }, [dispatch])

  // SignalR listener for translation completion
  useEffect(() => {
    const listener = message => {
      const currentId = targetCollectionIdRef.current

      if (message && message.collectionId) {
        const idMatches = currentId && (String(message.collectionId).toLowerCase() === String(currentId).toLowerCase())

        if (idMatches) {
          setIsTranslating(false)
          if (message.status === 'completed') {
            setIsSuccess(true)
            enqueueSnackbar('Translation completed successfully!', { variant: 'success' })
          } else {
            enqueueSnackbar(`Translation failed: ${message.error}`, { variant: 'error' })
          }
        }
      }
    }

    startConnection().then(() => {
      addTranslationCompleteListener(listener)
    })
    
    return () => {
      removeTranslationCompleteListener(listener)
    }
  }, []) // Empty deps so it NEVER unregisters unexpectedly

  const handleNext = () => {
    if (activeStep === 0 && !selectedCollectionId) {
      enqueueSnackbar('Please select a learning path to translate.', { variant: 'warning' })
      return
    }
    setActiveStep(prev => prev + 1)
  }

  const handleBack = () => {
    setActiveStep(prev => prev - 1)
  }

  const handleTranslate = () => {
    if (!selectedCollectionId || !selectedLanguage) return
    setIsTranslating(true)
    setTargetCollectionId(selectedCollectionId) // Fallback to original just in case
    enqueueSnackbar(`Translating to ${selectedLanguage}...`, { variant: 'info' })
    
    dispatch(
      collectionTranslateAction(selectedCollectionId, selectedLanguage, response => {
        // The API actually creates a NEW cloned collection and returns its ID!
        // We must update the target ID to the newly created one so SignalR maps correctly.
        const newId = response?.id || response?.data?.id
        const newName = response?.name || response?.data?.name
        if (newName) setTranslatedName(newName)
        if (newId) {
          setTargetCollectionId(newId)
        }
      })
    )
  }

  const handleReset = () => {
    setActiveStep(0)
    setSelectedCollectionId('')
    setTargetCollectionId('')
    setSelectedLanguage('es')
    setIsTranslating(false)
    setIsSuccess(false)
    setTranslatedName('')
  }

  const renderSettings = () => (
    <Box mt={3} mb={3}>
      <div className={classes.settingsToggle} onClick={() => setSettingsOpen(prev => !prev)}>
        <Box display="flex" alignItems="center">
          <SettingsIcon style={{ color: '#64748B', marginRight: '16px' }} />
          <div>
            <Typography style={{ fontWeight: 600, color: '#1E293B', fontSize: '15px', lineHeight: 1.2 }}>
              Connection Settings
            </Typography>
            <Typography style={{ color: '#64748B', fontSize: '13px', marginTop: '2px' }}>
              Gemini API Key (BYOK)
            </Typography>
          </div>
        </Box>
        {settingsOpen ? <KeyboardArrowUpIcon /> : <KeyboardArrowDownIcon />}
      </div>
      <Collapse in={settingsOpen}>
        <div className={classes.settingsContent}>
          <SettingsPanel />
        </div>
      </Collapse>
    </Box>
  )

  const renderStep = () => {
    switch (activeStep) {
      case 0:
        return (
          <Box display="flex" flexDirection="column" gap="16px">
            <Typography variant="h6">Choose a Collection to Translate</Typography>
            <Typography variant="body2" color="textSecondary" style={{ marginBottom: '16px' }}>
              Select from your personal learning paths to begin the translation process.
            </Typography>
            {loadingCollections ? (
              <CircularProgress size={24} />
            ) : (
              <FormControl variant="outlined" fullWidth>
                <InputLabel>Collection</InputLabel>
                <Select
                  value={selectedCollectionId}
                  onChange={(e) => setSelectedCollectionId(e.target.value)}
                  label="Learning path"
                >
                  {collections.map(col => (
                    <MenuItem key={col.id} value={col.id}>{col.collectionName || col.name}</MenuItem>
                  ))}
                  {collections.length === 0 && (
                    <MenuItem disabled value="">No collections available</MenuItem>
                  )}
                </Select>
              </FormControl>
            )}
            <Box mt={3} display="flex" justifyContent="flex-end">
              <Button 
                variant="contained" 
                color="primary" 
                onClick={handleNext} 
                disabled={!selectedCollectionId}
                className={classes.primaryButton}
              >
                Next Step
              </Button>
            </Box>
          </Box>
        )
      case 1:
        return (
          <Box display="flex" flexDirection="column" gap="16px">
            <Typography variant="h6">Configure Translation Settings</Typography>
            <Typography variant="body2" color="textSecondary">
              Set the target language for your learning path. We recommend setting up your own Gemini API key for reliable and faster translations.
            </Typography>
            
            {renderSettings()}

            <FormControl variant="outlined" fullWidth style={{ marginTop: '16px' }}>
              <InputLabel>Target Language</InputLabel>
              <Select
                value={selectedLanguage}
                onChange={(e) => setSelectedLanguage(e.target.value)}
                label="Target Language"
              >
                {languages.map(lang => (
                  <MenuItem key={lang.countryCode} value={lang.countryCode}>
                    {lang.name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            <Box mt={4} display="flex" justifyContent="space-between">
              <Button onClick={handleBack} className={classes.secondaryButton}>
                Back
              </Button>
              <Button 
                variant="contained" 
                color="primary" 
                onClick={handleNext}
                className={classes.primaryButton}
              >
                Next Step
              </Button>
            </Box>
          </Box>
        )
      case 2:
        if (isSuccess) {
          const slug = slugify(translatedName || 'collection')
          const shareUrl = `${URL.APP_URL}category/${slug}-1/${targetCollectionId}`
          const adminUrl = `${window.location.origin}/collection/${targetCollectionId}/mini-courses`
          
          return (
            <Box display="flex" flexDirection="column" alignItems="center" textAlign="center" py={4}>
              <CheckCircleIcon style={{ fontSize: '72px', color: '#10B981', marginBottom: '24px' }} />
              <Typography variant="h4" style={{ fontWeight: 800, color: '#1E293B', letterSpacing: '-0.02em' }}>
                Translation Complete!
              </Typography>
              <Typography style={{ color: '#64748B', fontSize: '16px', marginTop: '12px', marginBottom: '32px' }}>
                Your learning path has been successfully translated.
              </Typography>

              <Box display="flex" flexDirection="column" alignItems="center" width="100%" maxWidth="500px">
                <LinkCard classes={classes} label="Admin Link" url={adminUrl} isExternal={false} />
                <LinkCard classes={classes} label="Share Link" url={shareUrl} isExternal={true} />
              </Box>

              <Box mt={4}>
                <Button onClick={handleReset} variant="contained" color="primary" className={classes.primaryButton}>
                  Translate Another Learning Path
                </Button>
              </Box>
            </Box>
          )
        }

        return (
          <Box display="flex" flexDirection="column" gap="16px" alignItems="center" textAlign="center" py={4}>
            <Typography variant="h5" style={{ fontWeight: 'bold' }}>Ready to Translate</Typography>
            <Typography variant="body1" color="textSecondary" style={{ maxWidth: 400 }}>
              You are about to translate the selected collection to <b>{languages.find(l => l.countryCode === selectedLanguage)?.name || selectedLanguage}</b>.
              The translation will run in the background.
            </Typography>

            <Box mt={4}>
              <Button
                variant="contained"
                color="primary"
                onClick={handleTranslate}
                disabled={isTranslating}
                className={classes.primaryButton}
                style={{ padding: '0 48px' }}
              >
                {isTranslating ? <CircularProgress size={24} color="inherit" /> : 'Start Translation'}
              </Button>
            </Box>
            
            {!isTranslating && (
              <Box mt={2}>
                <Button onClick={handleReset} color="secondary" variant="text">
                  Start Over
                </Button>
              </Box>
            )}
            
            {isTranslating && (
               <Typography variant="body2" color="textSecondary" style={{ marginTop: '16px' }}>
                 This may take a few minutes. You will be notified when it's completed.
               </Typography>
            )}
          </Box>
        )
      default:
        return null
    }
  }

  return (
    <div className={classes.root}>
      <div className={classes.body}>
        <div className={classes.header}>
          <Typography className={classes.title}>Collection Translator</Typography>
          <Typography className={classes.subtitle}>Translate your existing collections into any target language with AI.</Typography>
        </div>

        <Paper className={classes.stepperPaper} elevation={0}>
          <Stepper activeStep={activeStep} alternativeLabel className={classes.stepper}>
            {STEPS.map(label => (
              <Step key={label}><StepLabel>{label}</StepLabel></Step>
            ))}
          </Stepper>
        </Paper>

        <div className={classes.card}>
          {renderStep()}
        </div>
      </div>
    </div>
  )
}

export default withStyles(styles)(CollectionTranslate)
